/**
 * What a Transfers drawer row says about its job (UI/UX fix round A, reviews A13 / U14): the operation and its target
 * as the title, the connection's alias and the remote path as a second line — so two jobs with the same file name on
 * different accounts stay apart — and source → destination in full for the tooltip.
 */
import type { TransferEndpoint, TransferJob } from '../../../types/azureStorageTransfers';
import { storageResourcePath } from '../../workspace/storage-explorer/utils/storagePaths';

const OPERATION: Record<TransferJob['kind'], string> = { download: 'Download', upload: 'Upload', copy: 'Copy', delete: 'Delete', acl: 'Access control', export: 'Export' };

/** C1 (N26): an access-control job names its mode — "Replace access (recursive)" — not only "Access control". */
const ACL_OPERATION: Record<'set' | 'modify' | 'remove', string> = { set: 'Replace access (recursive)', modify: 'Merge access (recursive)', remove: 'Remove access (recursive)' };

export const transferOperationLabel = (kind: TransferJob['kind'], acl?: TransferJob['acl']): string =>
  kind === 'acl' && acl ? ACL_OPERATION[acl.change.mode] : OPERATION[kind];

/** "0 of 1 item", "3 of 12 items" (C1 N26: it read "0 / 1 items"). */
export const transferItemsText = (done: number, total: number): string => `${done} of ${total} item${total === 1 ? '' : 's'}`;

/** The job's remote side: what a download or an export reads, what anything else writes to or changes. */
export function remoteEndpointOf(job: TransferJob): Extract<TransferEndpoint, { kind: 'remote' }> | undefined {
  const endpoint = job.kind === 'download' || job.kind === 'export' ? job.source : job.destination;
  return endpoint.kind === 'remote' ? endpoint : job.source.kind === 'remote' ? job.source : job.destination.kind === 'remote' ? job.destination : undefined;
}

function endpointText(endpoint: TransferEndpoint, aliasOf: (connectionId: string) => string): string {
  if (endpoint.kind === 'local') return endpoint.path;
  const path = storageResourcePath(endpoint.resource);
  return path ? `${aliasOf(endpoint.connectionId)}: ${path}` : aliasOf(endpoint.connectionId);
}

/** "Contoso prod · images/2026/report.csv" — the second line of a row. */
export function transferLocationLine(job: TransferJob, aliasOf: (connectionId: string) => string): string {
  const remote = remoteEndpointOf(job);
  if (!remote) return aliasOf(job.connectionId);
  const path = storageResourcePath(remote.resource);
  return path ? `${aliasOf(remote.connectionId)} · ${path}` : aliasOf(remote.connectionId);
}

/** "C:\\Users\\me\\report.csv → Contoso prod: images/report.csv" — the full route, for the row's tooltip. */
export function transferRoute(job: TransferJob, aliasOf: (connectionId: string) => string): string {
  if (job.kind === 'delete' || job.kind === 'acl') return endpointText(job.destination, aliasOf);
  return `${endpointText(job.source, aliasOf)} → ${endpointText(job.destination, aliasOf)}`;
}
